import {Autocomplete, FormControl, TextField, Typography} from '@mui/material';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import FirestoreManager from '../../api/firebaseManager';
import {useAddepar} from '../../hooks/use-adepar';
import '../../styles/plaid.css';

const FROM_ADDEPAR_OPTIONS = [
  {value: 'Not Receiving', label: 'Not Receiving'},
  {value: 'Receiving', label: 'Receiving'},
  {value: 'Rolling To Owner', label: 'Rolling To Owner'},
];

const FOREST_NOT_SENDING = 'Not Sending Data';

/** Shared Autocomplete styling: white background (matches BridgeFTModal / new-user-modal pattern) */
const autocompleteSx = {
  '& .MuiFilledInput-root': {backgroundColor: 'white'},
};
const autocompletePaperSx = {backgroundColor: 'white'};

/**
 * Flatten the portfolio tree into table rows.
 * Each node becomes one row; we keep:
 * - full path (addeparStructure) for keys/mappings
 * - name and depth for display so we don't repeat the full hierarchy every row
 */
function flattenPortfolioTree(nodes, pathParts = [], rowId = {current: 0}) {
  const rows = [];
  if (!Array.isArray(nodes)) return rows;

  for (const node of nodes) {
    const name = node.name ?? '—';
    const path = pathParts.length ? [...pathParts, name].join(' › ') : name;
    const columns = node.columns || {};
    const entityId = node.entity_id ?? columns.node_id ?? null;
    const ownerId = columns.direct_owner_id ?? null;
    const depth = pathParts.length; // 0 = root, 1 = child, etc.

    rows.push({
      key: `row-${rowId.current++}`,
      addeparStructure: path, // full hierarchy path for mapping keys
      name,
      depth,
      ownerId: ownerId != null ? String(ownerId) : '—',
      entityId: entityId != null ? String(entityId) : '—',
    });

    if (Array.isArray(node.children) && node.children.length > 0) {
      rows.push(
        ...flattenPortfolioTree(node.children, [...pathParts, name], rowId),
      );
    }
  }
  return rows;
}

/**
 * Extract flattened rows from API response.
 * Response shape: { success, data: { meta, data: { attributes: { total: { children[] } } } } }
 */
function getRowsFromResponse(response) {
  if (!response || response === false) return [];
  const payload = response.success === true ? response.data : response;
  const total = payload?.data?.attributes?.total;
  const children = total?.children;
  if (!Array.isArray(children)) return [];
  return flattenPortfolioTree(children);
}

/**
 * Parse Forest accounts from addepar-assetLiabilities response.
 * Shape: { data: [...], RowCount, Rows: [{ Id, Name, ... }] }
 */
function getForestAccountOptions(apiResult) {
  if (!apiResult || !Array.isArray(apiResult.data.Rows)) return [];
  return apiResult.data.Rows.map(r => ({value: r.Id, label: r.Name ?? r.Id}));
}

export default function AddeparMappingsPage({user}) {
  const navigate = useNavigate();
  const {connection_id, entity_id} = useParams();
  const {
    state: {connections},
    editConnection,
  } = useAddepar();
  const connection = connections[connection_id] || {};
  const entity_mappings = connection.entity_mappings || [];
  const account_mappings = connection.account_mappings || [];
  const entity =
    entity_mappings.find(e => String(e.entity_id) === String(entity_id)) || {};
  const entity_name = entity.entity_name || '';
  const customer_name = connection.customerID || '';

  // console.log({customer_name, entity_id, connection});

  const [portfolioSummary, setPortfolioSummary] = useState([]);
  const [forestAccountOptions, setForestAccountOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [portfolioRes, forestRes] = await Promise.all([
      FirestoreManager.getPortfolioSummary({
        connection_id,
        portfolio_id: entity_id,
        // connection_id: 'dbbd2a99-a4c1-41ca-9d18-c342ff459843',
      }),
      FirestoreManager.getAssetLiabilities({
        clientName: customer_name,
        entityName: entity_name,
        accountClassCodes: [1, 2],
      }),
    ]);

    setLoading(false);
    if (portfolioRes === false) {
      setError('Failed to load portfolio summary');
      return;
    }
    setPortfolioSummary(getRowsFromResponse(portfolioRes));

    if (forestRes && Array.isArray(forestRes.data.Rows)) {
      setForestAccountOptions(getForestAccountOptions(forestRes));
    } else {
      setForestAccountOptions([]);
    }
  }, [connection_id, customer_name, entity_name, entity_id]);

  useEffect(() => {
    if (!connection_id || !entity_name) return;
    loadData();
  }, [connection_id, entity_name, loadData]);

  const getMappingForRow = row => {
    const portfolio_id =
      row.entityId && row.entityId !== '—' ? String(row.entityId) : null;
    const mappingEntityId = entity.entity_id;

    const m = account_mappings.find(am => {
      if (!am) return false;
      const sameEntity =
        String(am.entity_id) === String(mappingEntityId ?? '');
      const storedPortfolioId =
        am.portfolio_id != null ? String(am.portfolio_id) : null;
      const samePortfolio = storedPortfolioId === portfolio_id;
      return sameEntity && samePortfolio;
    });

    return {
      forestAccountId: m?.forest_account_id ?? null,
      fromAddepar: m?.from_addepar ?? 'Not Receiving',
    };
  };

  const updateMapping = async (row, field, value) => {
    const {addeparStructure, ownerId, entityId, name} = row;
    const mappingEntityId = entity.entity_id;
    const entity_name = entity.entity_name;
    const portfolio_id =
      entityId && entityId !== '—' ? String(entityId) : null;

    // Map UI field names to stored keys
    const storageField =
      field === 'forestAccountId'
        ? 'forest_account_id'
        : field === 'fromAddepar'
        ? 'from_addepar'
        : field;

    const existingIndex = account_mappings.findIndex(am => {
      if (!am) return false;
      const sameEntity =
        String(am.entity_id) === String(mappingEntityId ?? '');
      const storedPortfolioId =
        am.portfolio_id != null ? String(am.portfolio_id) : null;
      const samePortfolio = storedPortfolioId === portfolio_id;
      return sameEntity && samePortfolio;
    });

    const base =
      existingIndex >= 0 ? account_mappings[existingIndex] : {};

    let nextRecord = {
      ...base,
      // Core fields other APIs expect
      account_name: base.account_name || name || addeparStructure,
      entity_id: mappingEntityId,
      entity_name,
      owner_id: ownerId,
      // New field linking to the Addepar portfolio node
      portfolio_id,
    };

    if (storageField === 'forest_account_id') {
      if (value === FOREST_NOT_SENDING || !value) {
        nextRecord.forest_account_id = null;
      } else {
        nextRecord.forest_account_id = value;
      }
    } else if (storageField === 'from_addepar') {
      nextRecord.from_addepar = value || 'Not Receiving';
    } else {
      nextRecord[storageField] = value;
    }

    if (nextRecord.from_addepar == null) {
      nextRecord.from_addepar = 'Not Receiving';
    }

    const nextAccountMappings =
      existingIndex >= 0
        ? account_mappings.map((am, idx) =>
            idx === existingIndex ? nextRecord : am,
          )
        : [...account_mappings, nextRecord];

    const updatedConnection = {
      ...connection,
      account_mappings: nextAccountMappings,
    };
    const {success} = await editConnection(updatedConnection);
    if (!success) {
      window.alert('Failed to save mapping.');
    }
  };

  return (
    <div className='page-container'>
      <div
        className='flex-row justify-between'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <div style={{flexGrow: 1}}>
          <Typography variant='h6' style={{marginBottom: '12px'}}>
            Addepar Mappings – {entity_name || 'Entity'}
          </Typography>
          {loading && <Typography color='textSecondary'>Loading...</Typography>}
          {error && (
            <Typography color='error' style={{marginBottom: '12px'}}>
              {error}
            </Typography>
          )}
          {!loading && !error && (
            <div className='addepar-mappings-table' style={{overflowX: 'auto'}}>
              <table style={{width: '100%'}}>
                <thead>
                  <tr>
                    <th>Forest To Addepar Account</th>
                    <th>Addepar Structure</th>
                    <th>Owner Id</th>
                    <th>Entity Id</th>
                    <th>From Addepar</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioSummary.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{textAlign: 'center', padding: 16}}
                      >
                        No portfolio data. Check connection and try again.
                      </td>
                    </tr>
                  ) : (
                  portfolioSummary.map((row, rowIndex) => {
                    const {forestAccountId, fromAddepar} = getMappingForRow(
                      row,
                    );
                      return (
                        <tr key={row.key}>
                          <td>
                            <FormControl fullWidth size='small'>
                              <Autocomplete
                                options={[
                                  {
                                    value: FOREST_NOT_SENDING,
                                    label: FOREST_NOT_SENDING,
                                  },
                                  ...forestAccountOptions,
                                ]}
                                value={
                                  forestAccountId == null ||
                                  forestAccountId === ''
                                    ? {
                                        value: FOREST_NOT_SENDING,
                                        label: FOREST_NOT_SENDING,
                                      }
                                    : (forestAccountOptions.find(
                                        o => o.value === forestAccountId,
                                      ) ?? {
                                        value: forestAccountId,
                                        label: forestAccountId,
                                      })
                                }
                                onChange={(_, newValue) =>
                                  updateMapping(
                                    row,
                                    'forestAccountId',
                                    newValue?.value ?? null,
                                  )
                                }
                                getOptionLabel={opt => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) =>
                                  a?.value === b?.value
                                }
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    variant='filled'
                                    size='small'
                                    sx={autocompleteSx}
                                    inputProps={{
                                      ...params.inputProps,
                                      'data-mapping-row-index': rowIndex,
                                      'data-mapping-col': 'forest',
                                      onKeyDown: e => {
                                        // Preserve Autocomplete's internal key handling
                                        if (params.inputProps.onKeyDown) {
                                          params.inputProps.onKeyDown(e);
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const next = document.querySelector(
                                            `[data-mapping-row-index='${rowIndex + 1}'][data-mapping-col='forest']`,
                                          );
                                          if (next instanceof HTMLElement) {
                                            next.focus();
                                          }
                                        }
                                      },
                                    }}
                                  />
                                )}
                                slotProps={{
                                  paper: {sx: autocompletePaperSx},
                                }}
                              />
                            </FormControl>
                          </td>
                          <td>
                            <div
                              style={{
                                paddingLeft: `${(row.depth || 0) * 16}px`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.name || row.addeparStructure}
                            </div>
                          </td>
                          <td>{row.ownerId}</td>
                          <td>{row.entityId}</td>
                          <td>
                            <FormControl fullWidth size='small'>
                              <Autocomplete
                                options={FROM_ADDEPAR_OPTIONS}
                                value={
                                  FROM_ADDEPAR_OPTIONS.find(
                                    o => o.value === fromAddepar,
                                  ) ?? {
                                    value: fromAddepar,
                                    label: fromAddepar,
                                  }
                                }
                                onChange={(_, newValue) =>
                                  updateMapping(
                                    row,
                                    'fromAddepar',
                                    newValue?.value ?? 'Not Receiving',
                                  )
                                }
                                getOptionLabel={opt => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) =>
                                  a?.value === b?.value
                                }
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    variant='filled'
                                    size='small'
                                    sx={autocompleteSx}
                                    inputProps={{
                                      ...params.inputProps,
                                      'data-mapping-row-index': rowIndex,
                                      'data-mapping-col': 'from',
                                      onKeyDown: e => {
                                        if (params.inputProps.onKeyDown) {
                                          params.inputProps.onKeyDown(e);
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const next = document.querySelector(
                                            `[data-mapping-row-index='${rowIndex + 1}'][data-mapping-col='from']`,
                                          );
                                          if (next instanceof HTMLElement) {
                                            next.focus();
                                          }
                                        }
                                      },
                                    }}
                                  />
                                )}
                                slotProps={{
                                  paper: {sx: autocompletePaperSx},
                                }}
                              />
                            </FormControl>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            marginLeft: '36px',
          }}
        >
          <button
            className='disconnect'
            onClick={() => navigate(`/addepar/${connection_id}`)}
            style={{marginBottom: '16px'}}
          >
            Back to Addepar
          </button>
          <button
            className='disconnect'
            onClick={() => navigate('/dashboard')}
            style={{marginBottom: '16px'}}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
